import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PageHeader from '../../components/PageHeader';
import FilterToolbar from '../../components/FilterToolbar';
import SectionCard from '../../components/SectionCard';
import InvDataGrid from '../../components/InvDataGrid';
import InvButton from '../../components/InvButton';
import BatchChip from '../../components/BatchChip';
import { getLedger, getStores } from '../../api/stockLedger';
import InvItemAutocomplete from '../../components/InvItemAutocomplete';
import useAssignedStoreOptions from '../../hooks/useAssignedStoreOptions';
import useItemNames from '../../hooks/useItemNames';
import { exportToCsv, formatDateTime } from '../../utils';
import { IPD_COLORS } from '../../theme';
import { MOVEMENT_TYPES } from '../../constants';

const MOVEMENT_COLORS = {
  RECEIPT: IPD_COLORS.success.text, ISSUE: IPD_COLORS.warning.text,
  TRANSFER_IN: IPD_COLORS.primary, TRANSFER_OUT: IPD_COLORS.textSecondary,
  RETURN: '#8B5CF6', CONSUMPTION: IPD_COLORS.error.text,
  ADJUSTMENT: IPD_COLORS.info.text, RESERVATION: IPD_COLORS.warning.text,
};

export default function LedgerView() {
  const { resolveItem } = useItemNames();
  const [entries, setEntries] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {
    storeLocked, activeStoreId, setActiveStoreId, isStoreScoped, storeOptions,
  } = useAssignedStoreOptions(stores, { allLabel: 'All Stores' });
  const [filters, setFilters] = useState({
    store_id: activeStoreId || '', item_id: '', movement_type: '',
    from_date: '', to_date: '',
  });

  useEffect(() => {
    // API: GET /inventory/stores/
    getStores().then(d => setStores(Array.isArray(d) ? d : (d?.items || []))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = {};
      Object.keys(filters).forEach(k => { if (filters[k]) p[k] = filters[k]; });
      // API: GET /inventory/stock/ledger/
      const data = await getLedger(p);
      setEntries(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isStoreScoped && activeStoreId) {
      setFilters(f => (f.store_id === activeStoreId ? f : { ...f, store_id: activeStoreId }));
    }
  }, [isStoreScoped, activeStoreId]);

  const lockedStoreLabel = storeOptions.find(o => o.value === filters.store_id)?.label || '';

  // The locked store is not a filter the user chose, so it must not keep Reset enabled.
  const hasActiveFilters = Object.entries(filters)
    .some(([k, v]) => Boolean(v) && !(storeLocked && k === 'store_id'));

  function handleResetFilters() {
    setFilters({
      store_id: isStoreScoped ? (activeStoreId || '') : '', item_id: '', movement_type: '',
      from_date: '', to_date: '',
    });
  }

  const columns = [
    { field: 'created_at', headerName: 'Date', width: 150, valueFormatter: p => formatDateTime(p.value) },
    { field: 'movement_type', headerName: 'Type', width: 150,
      renderCell: p => (
        <Typography sx={{ color: MOVEMENT_COLORS[p.value] || IPD_COLORS.textSecondary, fontWeight: 600, fontSize: 12 }}>
          {p.value?.replace(/_/g, ' ')}
        </Typography>
      ) },
    { field: 'item_name', headerName: 'Item', flex: 1, valueGetter: p => resolveItem(p.row) },
    { field: 'batch', headerName: 'Batch', width: 180,
      renderCell: p => <BatchChip batchNumber={p.row.batch_number} expiryDate={p.row.expiry_date} /> },
    { field: 'quantity_in', headerName: 'Qty In', width: 90, align: 'right', headerAlign: 'right',
      renderCell: p => p.value ? <Typography sx={{ color: IPD_COLORS.success.text, fontWeight: 600, fontSize: 13 }}>{p.value}</Typography> : <span style={{ color: IPD_COLORS.textSecondary }}>—</span> },
    { field: 'quantity_out', headerName: 'Qty Out', width: 90, align: 'right', headerAlign: 'right',
      renderCell: p => p.value ? <Typography sx={{ color: IPD_COLORS.warning.text, fontWeight: 600, fontSize: 13 }}>{p.value}</Typography> : <span style={{ color: IPD_COLORS.textSecondary }}>—</span> },
    { field: 'balance', headerName: 'Balance', width: 90, align: 'right', headerAlign: 'right' },
    { field: 'reference_type', headerName: 'Ref.', width: 110 },
  ];

  return (
    <Box>
      <PageHeader eyebrow="Inventory Management" title="Stock Ledger"
        description={`${entries.length} entries`}
        actions={
          <InvButton startIcon={<DownloadIcon />} variant="outlined" tone="teal" size="small"
            onClick={() => exportToCsv('ledger.csv', entries)}>Export</InvButton>
        }
      />
      <FilterToolbar>
        {storeLocked ? (
          <FilterToolbar.StaticValue label="Store" width="wide" value={lockedStoreLabel} />
        ) : (
          <FilterToolbar.Filter
            label="Store"
            width="wide"
            value={filters.store_id}
            onChange={e => {
              setFilters(f => ({ ...f, store_id: e.target.value }));
              if (isStoreScoped) setActiveStoreId(e.target.value);
            }}
            options={storeOptions}
          />
        )}
        <FilterToolbar.Filter
          label="Movement Type"
          width="wide"
          value={filters.movement_type}
          onChange={e => setFilters(f => ({ ...f, movement_type: e.target.value }))}
          options={[{ value: '', label: 'All Types' }, ...MOVEMENT_TYPES.map(t => ({ value: t, label: t }))]}
        />
        <FilterToolbar.DateFilter
          label="From Date"
          value={filters.from_date}
          onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))}
        />
        <FilterToolbar.DateFilter
          label="To Date"
          value={filters.to_date}
          onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))}
        />
        <FilterToolbar.Slot width={260}>
          <InvItemAutocomplete
            value={filters.item_id}
            onChange={(itemId) => setFilters(f => ({ ...f, item_id: itemId || '' }))}
            placeholder="Filter by item…"
          />
        </FilterToolbar.Slot>
        <FilterToolbar.Reset onClick={handleResetFilters} disabled={!hasActiveFilters} />
      </FilterToolbar>
      <SectionCard title="">
        <InvDataGrid rows={entries} columns={columns} loading={loading} error={error} onRetry={load}
          getRowId={r => r.ledger_id || r.id || Math.random()} emptyMessage="No ledger entries found." />
      </SectionCard>
    </Box>
  );
}
